import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyP2Bold } from '@heidi/design-system/typography';
import { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import type { MermaidErrorComponentProps } from 'streamdown';

import { MermaidStreamingContext } from './MermaidStreamingContext';

/**
 * Custom Mermaid error component for Streamdown.
 *
 * - While streaming: render nothing. Transient parse errors during incremental
 *   token emission shouldn't flicker any UI; the diagram simply appears once
 *   the stream completes and the block parses.
 * - After streaming: render a validation-warning card with the underlying
 *   parse error and a "Try again" button (Streamdown re-runs the render on
 *   retry).
 */
export const MermaidErrorComponent = ({ error, retry }: MermaidErrorComponentProps) => {
  const isStreaming = useContext(MermaidStreamingContext);

  if (isStreaming) {
    return null;
  }

  return (
    <div
      className="my-4 flex flex-col gap-2 rounded-lg border border-validation-warning-border bg-validation-warning-muted p-3"
      data-testid="mermaid-error"
      role="alert"
    >
      <TypographyP2Bold className="text-validation-warning">
        {/* eslint-disable-next-line formatjs/enforce-id -- the id was minted
            while this file sat in the ESLint-ignored Chat tree and is already
            live in Crowdin; re-minting it to match the hash would orphan every
            translation of this string. */}
        <FormattedMessage id="mWxQk7Lp9R" defaultMessage="Could not render diagram" />
      </TypographyP2Bold>
      <TypographyCaption className="break-words text-text-secondary">
        {error}
      </TypographyCaption>
      <Button variant="outline" size="sm" onClick={retry} className="mt-1 w-fit">
        <FormattedMessage defaultMessage="Try again" id="FazwRldA7z" />
      </Button>
    </div>
  );
};
